<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav />

    <div class="flex flex-col lg:flex-row gap-4 min-h-[70vh]">
      <aside class="w-full lg:w-[20%] shrink-0">
        <nav class="bg-white rounded-lg border border-gray-100 shadow-sm p-2 space-y-1">
          <router-link
            v-for="item in settingsMenu"
            :key="item.route"
            :to="{ name: item.route }"
            class="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition"
            :class="isSettingsActive(item)
              ? 'bg-gray-900 text-white hover:bg-gray-900 hover:text-white'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-700'"
          >
            {{ item.label }}
          </router-link>
        </nav>
      </aside>

      <section class="flex-1 min-w-0 bg-white rounded-lg border border-gray-100 shadow-sm p-4 sm:p-6">
        <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">{{ $t('smm.logs.title') }}</h2>
            <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.logs.subtitle') }}</p>
          </div>
          <button
            type="button"
            class="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
            @click="load"
          >
            {{ $t('common.refresh') }}
          </button>
        </div>

        <div class="flex flex-wrap gap-2 mb-4">
          <select v-model="source" class="border rounded-lg px-3 py-2 text-sm bg-white" @change="page = 1; load()">
            <option value="all">{{ $t('smm.logs.source_all') }}</option>
            <option value="smm-ai">{{ $t('smm.logs.source_smm_ai') }}</option>
            <option value="laravel">{{ $t('smm.logs.source_laravel') }}</option>
            <option value="prompts">{{ $t('smm.logs.source_prompts') }}</option>
          </select>
          <select v-model="level" class="border rounded-lg px-3 py-2 text-sm bg-white" @change="page = 1; load()">
            <option value="">{{ $t('smm.logs.level_all') }}</option>
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="error">error</option>
            <option value="critical">critical</option>
          </select>
          <input
            v-model="query"
            type="search"
            class="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[12rem]"
            :placeholder="$t('smm.logs.search_placeholder')"
            @keyup.enter="page = 1; load()"
          />
          <button
            type="button"
            class="px-3 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-800"
            @click="page = 1; load()"
          >
            {{ $t('smm.logs.search') }}
          </button>
        </div>

        <p v-if="error" class="text-sm text-red-600 mb-3">{{ error }}</p>
        <div v-if="loading" class="py-10 text-center text-gray-500 text-sm">{{ $t('common.loading') }}</div>
        <div v-else-if="!entries.length" class="py-10 text-center text-gray-500 text-sm">{{ $t('smm.logs.empty') }}</div>

        <div v-else class="space-y-2">
          <article
            v-for="entry in entries"
            :key="entry.id"
            class="border rounded-lg p-3 hover:border-gray-300 transition"
            :class="levelClass(entry.level)"
          >
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2 text-[11px] text-gray-500 mb-1">
                  <span class="font-mono">{{ entry.logged_at }}</span>
                  <span class="uppercase tracking-wide px-1.5 py-0.5 rounded bg-black/5">{{ entry.level }}</span>
                  <span v-if="entry.type === 'prompt'" class="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">prompt</span>
                  <span class="truncate">{{ entry.file }}</span>
                </div>
                <p class="text-sm text-gray-900 font-medium break-words">{{ entry.message }}</p>
              </div>
              <button
                v-if="entry.path"
                type="button"
                class="shrink-0 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
                @click="openDetail(entry)"
              >
                {{ $t('smm.logs.open') }}
              </button>
            </div>
            <pre
              v-if="entry.context && expandedId === entry.id"
              class="mt-2 text-xs bg-gray-50 rounded-lg p-2 overflow-x-auto whitespace-pre-wrap max-h-64"
            >{{ entry.context }}</pre>
            <button
              v-if="entry.context && entry.type === 'log'"
              type="button"
              class="mt-1 text-xs text-gray-500 hover:text-gray-800"
              @click="expandedId = expandedId === entry.id ? null : entry.id"
            >
              {{ expandedId === entry.id ? $t('common.hide') : $t('smm.logs.show_context') }}
            </button>
          </article>
        </div>

        <div v-if="meta.last_page > 1" class="flex flex-wrap items-center justify-between gap-2 mt-4 pt-3 border-t border-gray-100">
          <p class="text-xs text-gray-500">
            {{ $t('smm.logs.page_of', { page: meta.current_page, pages: meta.last_page, total: meta.total }) }}
          </p>
          <div class="flex gap-2">
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40"
              :disabled="page <= 1 || loading"
              @click="page -= 1; load()"
            >
              ←
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40"
              :disabled="page >= meta.last_page || loading"
              @click="page += 1; load()"
            >
              →
            </button>
          </div>
        </div>
      </section>
    </div>

    <div
      v-if="detail"
      class="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      @click.self="detail = null"
    >
      <div class="bg-white w-full max-w-4xl max-h-[90vh] rounded-t-2xl sm:rounded-lg shadow-xl flex flex-col">
        <div class="flex items-center justify-between gap-2 p-4 border-b">
          <div class="min-w-0">
            <p class="font-medium text-gray-900 truncate">{{ detail.path }}</p>
            <p class="text-xs text-gray-500">{{ detail.size }} bytes <span v-if="detail.truncated">(truncated)</span></p>
          </div>
          <button type="button" class="text-sm px-3 py-1.5 rounded-lg hover:bg-gray-100" @click="detail = null">
            {{ $t('common.close') }}
          </button>
        </div>
        <pre class="flex-1 overflow-auto p-4 text-xs whitespace-pre-wrap bg-gray-50">{{ detail.content }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
import SmmSubNav from '@/components/smm/SmmSubNav.vue'

export default {
  components: { SmmSubNav },
  data: () => ({
    entries: [],
    meta: { current_page: 1, last_page: 1, total: 0, per_page: 30 },
    page: 1,
    source: 'all',
    level: '',
    query: '',
    loading: true,
    error: null,
    expandedId: null,
    detail: null,
  }),
  computed: {
    settingsMenu() {
      return [
        { route: 'SmmSettingsChannels', label: this.$t('smm.settings.menu_channels') },
        { route: 'SmmSettingsPrompts', label: this.$t('smm.settings.menu_prompts') },
        { route: 'SmmSettingsCampaigns', label: this.$t('smm.settings.menu_campaigns') },
        { route: 'SmmSettingsBusiness', label: this.$t('smm.settings.menu_business') },
        { route: 'SmmSettingsLogs', label: this.$t('smm.settings.menu_logs') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    isSettingsActive(item) {
      return this.$route.name === item.route
    },
    levelClass(level) {
      if (level === 'error' || level === 'critical' || level === 'alert' || level === 'emergency') {
        return 'border-red-200 bg-red-50/40'
      }
      if (level === 'warning') {
        return 'border-amber-200 bg-amber-50/40'
      }
      return 'border-gray-100 bg-white'
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get('/api/v1/smm/logs', {
          params: {
            page: this.page,
            per_page: 30,
            source: this.source,
            level: this.level || undefined,
            q: this.query || undefined,
          },
        })
        this.entries = data.data ?? []
        this.meta = data.meta ?? this.meta
        this.page = this.meta.current_page || this.page
      } catch (e) {
        this.error = e.response?.data?.message || this.$t('common.error')
      } finally {
        this.loading = false
      }
    },
    async openDetail(entry) {
      if (!entry?.path) return
      this.error = null
      try {
        const { data } = await axios.get('/api/v1/smm/logs/content', {
          params: { path: entry.path },
        })
        this.detail = data.data
      } catch (e) {
        this.error = e.response?.data?.message || this.$t('common.error')
      }
    },
  },
}
</script>
